import { useState, useEffect, useContext } from "react";
import { UserContext } from "../../Context";
import { useNavigate, useLocation } from "react-router-dom";
import { CommonTable, Loader, Tooltip } from "../../Components/Common";
import { FiInfo, FiFilter, FiDownload, FiEdit } from "react-icons/fi";
import {
  SALARY_SLIP_LABELS,
  SALARY_SLIP_COLUMNS,
  getSlipStatusColor,
  SALARY_SLIP_STATUS,
  SALARY_SLIP_STATUS_OPTIONS,
  ROLES,
} from "./constants";
import { formatCurrency, formatMonthYear, toProperCase } from "../../Utils";
import { usePayroll } from "./Hooks/usePayroll.js";
import { RESET_PAGINATION } from "../../Constants/Strings";
import { DETAIL_SALARY_SLIP, EDIT_SALARY_SLIP } from "../../routes.js";
import InfiniteScroll from "react-infinite-scroll-component";
import CommonFilter from "../../Components/Common/CommonFilter";
import { useSalarySlipPdf } from "./Hooks/useSalarySlipPdf";

const SalarySlipList = () => {
  const {
    loading,
    filters,
    setFilters,
    setPagination,
    pagination,
    payroll,
    setPayroll,
    fetchSalarySlips,
    loadMoreSalarySlips,
    addSalarySlip,
    notifyDownloadSalarySlip,
  } = usePayroll();
  const location = useLocation();
  const [selectedUserOption, setSelectedUserOption] = useState(() => {
    return (
      location.state?.selectedUser || null
    );
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
  };
  const { downloadSalarySlipPdf } = useSalarySlipPdf();
  const [payrollMonth, setPayrollMonth] = useState(getCurrentMonth());

  const handleRunPayroll = async () => {
    if (!payrollMonth) return;

    const success = await addSalarySlip({
      month: payrollMonth, // "YYYY-MM"
    });

    if (success) {
      loadSalarySlip();
    }
  };

  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const loadSalarySlip = async () => {
    const data = await fetchSalarySlips(
      { offset: 0, limit: pagination.limit },
      true
    );
    setPayroll(data);
  };

  const handleSlipDownload = async (slipId) => {
    await notifyDownloadSalarySlip(slipId);
  };

  const renderField = (key, row) => {
    if (key === "status") {
      return (
        <span
          className={`px-1.5 py-1 rounded-md text-xs font-medium ${getSlipStatusColor(
            row.status
          )}`}
        >
          {toProperCase(row.status)}
        </span>
      );
    }
    if (key === "month") {
      if (!row.start_date) return "-";
      return new Date(row.start_date).toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
    if (key === "action") {
      return (
        <div className="flex items-center gap-2">
          {/* Detail Button */}
          <Tooltip text={"View Salary Details"}>
            <button
              onClick={() => handleDetail(row.id)}
              className="h-5 w-5 text-green-500 cursor-pointer hover:text-green-700 transition-colors"
            >
              <FiInfo size={17} />
            </button>
          </Tooltip>
          <Tooltip text={"Download Salary Slip"}>
            <button
              className="h-5 w-5 text-green-500 cursor-pointer hover:text-green-700 transition-colors"
              onClick={() => {
                downloadSalarySlipPdf(row);
                handleSlipDownload(row.id);
              }}
            >
              <FiDownload className="text-md" />
            </button>
          </Tooltip>
          {(user?.role === ROLES.HR || user?.role === ROLES.ADMIN_ROLE ||  user?.role === ROLES.DIRECTOR_MANAGEMENT) && (
            <Tooltip
              text={`${row.status !== SALARY_SLIP_STATUS.APPROVED
                  ? "Edit Salary Details"
                  : "Can not be edited in approved state"
                }`}
            >
              <button
                disabled={row.status === SALARY_SLIP_STATUS.APPROVED}
                className={`h-5 w-5 cursor-pointer transition-colors 
                  ${row.status !== SALARY_SLIP_STATUS.APPROVED
                    ? "text-green-500 hover:text-green-700"
                    : "text-gray-400 cursor-not-allowed"
                  }`}
                onClick={() => row.status !== SALARY_SLIP_STATUS.APPROVED && handleEdit(row.id)}
              >
                <FiEdit className="text-md" />
              </button>
            </Tooltip>
          )}
        </div>
      );
    }
    return row[key] || "-";
  };

  const renderMobileCards = () => {
    return (
      <div className="md:hidden space-y-2 mt-2">
        {payroll.length === 0 ? (
          <p className="text-center text-gray-600">No Slips found.</p>
        ) : (
          payroll.map((row) => {
            return (
              <div
                key={row.id}
                className="border rounded-lg shadow-sm p-2 bg-white"
              >
                <div className="mb-2">
                  <p className="font-semibold text-primary flex justify-between">
                    <span> {row.username}</span>
                    <span>{formatCurrency(row.net)}</span>
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-900 font-normal">
                      {formatMonthYear(row.start_date)}
                    </span>
                  </div>
                  <p className="font-semibold text-primary flex justify-between">
                    <span
                      className={` rounded-md text-sm mt-2 font-medium ${getSlipStatusColor(
                        row.status
                      )}`}
                    >
                      {toProperCase(row.status)}
                    </span>
                    <div className="flex items-center gap-2">
                      <FiInfo
                        size={17}
                        onClick={() => handleDetail(row.id)}
                        className="h-5 w-5 text-green-500 cursor-pointer hover:text-green-700 transition-colors"
                      />
                      <button
                        className="flex items-center gap-1 text-gray-600"
                        onClick={() => {
                          downloadSalarySlipPdf(row);
                          handleSlipDownload(row.id);
                        }}
                      >
                        <FiDownload className="text-md" />
                      </button>
                      {(user?.role === ROLES.HR ||
                        user?.role === ROLES.ADMIN_ROLE) && (
                          <button
                            disabled={row.status !== SALARY_SLIP_STATUS.INITIATED}
                            className={`h-5 w-5 cursor-pointer transition-colors 
                            ${row.status === SALARY_SLIP_STATUS.INITIATED
                                ? "text-green-500 hover:text-green-700"
                                : "text-gray-400 cursor-not-allowed"
                              }`}
                            onClick={() =>
                              row.status === SALARY_SLIP_STATUS.INITIATED &&
                              handleEdit(row.id)
                            }
                          >
                            <FiEdit className="text-md" />
                          </button>
                        )}
                    </div>
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const handleDetail = (id) => {
    navigate(DETAIL_SALARY_SLIP.replace(":slipId", id));
  };

  const handleEdit = (id) => {
    navigate(EDIT_SALARY_SLIP.replace(":slipId", id));
  };

  const handleApplyFilters = () => {
    if (tempFilters.user === '') {
      return
    }
    setSelectedUserOption(tempFilters.user);
    tempFilters.user = tempFilters.user?.id;
    setFilters(tempFilters);
    setPagination(RESET_PAGINATION);
    setPayroll([]);
    setShowFilter(false);

    // Update navigation state for back button
    navigate(".", { state: { filters: tempFilters, selectedUser: tempFilters.user}, replace: true });
  };
  const handleClearFilters = () => {
    const cleared = { status: "", user: "" };
    setTempFilters(cleared);
    setFilters(cleared);
    setPagination(RESET_PAGINATION);
    setPayroll([]);

    navigate(".", { state: { filters: cleared }, replace: true });
  };

  const handleCancelFilters = () => {
    setTempFilters(filters);
    setShowFilter(false);
  };

  useEffect(() => {
    loadSalarySlip();
  }, [filters]);

  return (
    <div className="px-4 py-4">
      <div className="flex flex-col w-full">
        {/* Heading + Add button */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-primary">Salary Slip</h2>
          {(user.role === ROLES.HR || user.role === ROLES.ADMIN_ROLE) && (
            <div className="flex items-center gap-2">
              <button
                className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
                onClick={() => {
                  setTempFilters(filters);
                  setShowFilter(true);
                }}
              >
                <FiFilter className="text-xl" />
              </button>
            </div>
          )}
        </div>

        {/* Filter Modal */}
        {showFilter && (
          <CommonFilter
            title="Salary Slip Filter"
            tempFilters={tempFilters}
            setTempFilters={setTempFilters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            onCancel={handleCancelFilters}
            statusOptions={[
              { label: "All", value: "" },
              ...SALARY_SLIP_STATUS_OPTIONS,
            ]}
            useSearchableUser={true}
            selectedSearchableValue={selectedUserOption}
          />
        )}
      </div>
      {(user.role === ROLES.HR || user.role === ROLES.DIRECTOR_MANAGEMENT) && (
        <div className="flex gap-2 mb-1">
          <input
            type="month"
            value={payrollMonth}
            onChange={(e) => setPayrollMonth(e.target.value)}
            className="border px-2 py-1 h-10"
          />
          <button
            className="px-4 py-2 bg-green-500 text-white mb-1 h-10"
            onClick={handleRunPayroll}
          >
            Run Payroll
          </button>
        </div>
      )}
      <InfiniteScroll
        dataLength={payroll.length}
        next={loadMoreSalarySlips}
        hasMore={pagination.offset < pagination.count}
        scrollableTarget="scrollDiv"
      >
        <div
          className="h-[70vh] overflow-x-auto overflow-y-auto px-4 py-2 mt-3"
          id="scrollDiv"
        >
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          ) : (
            <>
              <div className="hidden md:block">
                <CommonTable
                  tableColumns={SALARY_SLIP_COLUMNS}
                  columnLabels={SALARY_SLIP_LABELS}
                  tableRows={payroll}
                  renderField={renderField}
                  headerClassName="bg-gray-100"
                  rowClick={null}
                />
              </div>
              {renderMobileCards()}
            </>
          )}
        </div>
      </InfiniteScroll>
      {/* Mobile Card View */}
    </div>
  );
};
export default SalarySlipList;
